"use client";

import { c as _c } from "react/compiler-runtime";
import DriveFileRenameOutline from "@mui/icons-material/DriveFileRenameOutline";
import { IconButton, InputAdornment, Popover, TextField, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { caseSlice } from "#/entities/argument/model/caseSlice";
import { useI18nContext } from "#/shared/hooks";
import { useAppDispatch } from "#/store/hooks";
const SAVE_DEBOUNCE_MS = 300;
type ArgumentRenameSuffixProps = {
  argumentId: string;
  labelDraft: string;
};
const normalizeLabel = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : trimmed;
};
export const ArgumentRenameSuffix: React.FC<ArgumentRenameSuffixProps> = t0 => {
  const $ = _c(53);
  const {
    argumentId,
    labelDraft
  } = t0;
  const {
    LL
  } = useI18nContext();
  const dispatch = useAppDispatch();
  const titleId = useId();
  const [anchorEl, setAnchorEl] = useState(null);
  const [localDraft, setLocalDraft] = useState(labelDraft);
  const debounceRef = useRef(null);
  let t1;
  if ($[0] !== argumentId || $[1] !== dispatch) {
    t1 = raw => {
      dispatch(caseSlice.actions.updateArgumentLabel({
        name: argumentId,
        label: normalizeLabel(raw)
      }));
    };
    $[0] = argumentId;
    $[1] = dispatch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const flushLabelToStore = t1;
  let t2;
  let t3;
  if ($[3] !== anchorEl || $[4] !== flushLabelToStore || $[5] !== localDraft) {
    t2 = () => {
      if (!anchorEl) {
        return;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        flushLabelToStore(localDraft);
      }, SAVE_DEBOUNCE_MS);
      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
      };
    };
    t3 = [anchorEl, flushLabelToStore, localDraft];
    $[3] = anchorEl;
    $[4] = flushLabelToStore;
    $[5] = localDraft;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== labelDraft) {
    t4 = event => {
      setLocalDraft(labelDraft);
      setAnchorEl(event.currentTarget);
    };
    $[8] = labelDraft;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleOpen = t4;
  let t5;
  if ($[10] !== flushLabelToStore || $[11] !== localDraft) {
    t5 = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      flushLabelToStore(localDraft);
      setAnchorEl(null);
    };
    $[10] = flushLabelToStore;
    $[11] = localDraft;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleClose = t5;
  const open = Boolean(anchorEl);
  let t6;
  if ($[13] !== LL) {
    t6 = LL.ARGUMENT_RENAME();
    $[13] = LL;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== LL) {
    t7 = LL.ARGUMENT_RENAME();
    $[15] = LL;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DriveFileRenameOutline fontSize="small" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== handleOpen || $[19] !== t7) {
    t9 = <IconButton edge="end" size="small" aria-label={t7} onClick={handleOpen}>{t8}</IconButton>;
    $[18] = handleOpen;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t9) {
    t10 = <Tooltip title={t6}>{t9}</Tooltip>;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      vertical: "bottom",
      horizontal: "right"
    };
    t12 = {
      vertical: "top",
      horizontal: "right"
    };
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      p: 1.5,
      minWidth: 220
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== titleId) {
    t14 = {
      paper: {
        "aria-labelledby": titleId,
        sx: t13
      }
    };
    $[27] = titleId;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== LL) {
    t15 = LL.ARGUMENT_DISPLAY_NAME();
    $[29] = LL;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15 || $[32] !== titleId) {
    t16 = <Typography id={titleId} variant="subtitle2" gutterBottom={true}>{t15}</Typography>;
    $[31] = t15;
    $[32] = titleId;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_0 => setLocalDraft(event_0.target.value);
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== LL) {
    t18 = LL.ARGUMENT_DISPLAY_NAME_PLACEHOLDER();
    $[35] = LL;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== LL) {
    t19 = LL.ARGUMENT_DISPLAY_NAME_HELPER();
    $[37] = LL;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== localDraft || $[40] !== t18 || $[41] !== t19) {
    t20 = <TextField autoFocus={true} size="small" fullWidth={true} value={localDraft} onChange={t17} placeholder={t18} helperText={t19} />;
    $[39] = localDraft;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== anchorEl || $[44] !== handleClose || $[45] !== open || $[46] !== t14 || $[47] !== t16 || $[48] !== t20) {
    t21 = <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={t11} transformOrigin={t12} slotProps={t14}>{t16}{t20}</Popover>;
    $[43] = anchorEl;
    $[44] = handleClose;
    $[45] = open;
    $[46] = t14;
    $[47] = t16;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t10 || $[51] !== t21) {
    t22 = <>{t10}{t21}</>;
    $[50] = t10;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  return t22;
};
export const argumentRenameEndAdornment = (argumentId: string, labelDraft: string): React.ReactNode => <InputAdornment position="end" sx={{
  margin: 0
}}>
    <ArgumentRenameSuffix argumentId={argumentId} labelDraft={labelDraft} />
  </InputAdornment>;